import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
  signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DrawerModule } from 'primeng/drawer';
import { SelectButton } from 'primeng/selectbutton';
import { NgkitSearchComponent } from '@wtw/angularkit/ngkit-search';
import { FgaIconComponent } from '../fga-icon/fga-icon.component';
import { FgaIconName, GROUP_ICON } from '../fga-icon/fga-icon.model';
import { EllipsisTitleDirective } from '../../directives/ellipsis-title.directive';
import { sortByLabel } from '../../utils/sorting';
import { ENTITY_TYPE, MEMBERSHIP_MODE } from '../../models/entity.model';
import { isScimManagedGroupId } from '../../models/group.model';
import {
  PICKER_MEMBER_FILTER,
  PICKER_MODE_VIEWS,
  PICKER_OWNER_FILTER,
  PickerConfig,
  PickerItem,
  PickerMemberFilter,
  PickerModeView,
  PickerOwnerFilter,
  PickerSavePayload,
  PickerTypeFilter
} from './fga-picker-panel.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    CheckboxModule,
    CommonModule,
    DrawerModule,
    EllipsisTitleDirective,
    FgaIconComponent,
    FormsModule,
    NgkitSearchComponent,
    SelectButton,
  ],
  selector: 'fga-picker-panel',
  styleUrls: ['./fga-picker-panel.component.scss'],
  templateUrl: './fga-picker-panel.component.html',
})
export class FgaPickerPanelComponent {
  readonly config = input.required<PickerConfig>();

  readonly closePanel = output<void>();

  readonly saveChange = output<PickerSavePayload>();

  readonly ownerFilter = signal<PickerOwnerFilter>(PICKER_OWNER_FILTER.ALL);

  readonly memberFilter = signal<PickerMemberFilter>(PICKER_MEMBER_FILTER.ALL);

  readonly filterText = signal<string>('');

  readonly stagedMembers = linkedSignal<PickerItem[] | undefined, PickerItem[]>({
    computation: (items, previous) => this.stageItems(items, previous),
    source: () => this.config().lists.members
  });

  readonly stagedOwnerUsers = linkedSignal<PickerItem[] | undefined, PickerItem[]>({
    computation: (items, previous) => this.stageItems(items, previous),
    source: () => this.config().lists.ownerUsers
  });
  
  readonly stagedOwnerGroups = linkedSignal<PickerItem[] | undefined, PickerItem[]>({
    computation: (items, previous) => this.stageItems(items, previous),
    source: () => this.config().lists.ownerGroups
  });

  readonly currentItems = computed<PickerItem[]>(() => {
    if (this.isMembersMode()) {
      return this.stagedMembers();
    }

    switch (this.ownerFilter()) {
      case PICKER_OWNER_FILTER.USER:
        return sortByLabel(this.stagedOwnerUsers());
      case PICKER_OWNER_FILTER.APPLICATION_GROUP:
        return sortByLabel(this.stagedOwnerGroups().filter((group) => !isScimManagedGroupId(group.id)));
      case PICKER_OWNER_FILTER.ENTERPRISE_GROUP:
        return sortByLabel(this.stagedOwnerGroups().filter((group) => isScimManagedGroupId(group.id)));
      default:
        return sortByLabel([...this.stagedOwnerUsers(), ...this.stagedOwnerGroups()]);
    }
  });

  readonly filteredItems = computed<PickerItem[]>(() => {
    const filter = this.filterText().trim().toLowerCase();
    const items = this.applyMemberTypeFilter(this.currentItems());

    if (!filter) {
      return items;
    }

    return items.filter((item) => {
      const label = (item.label ?? '').toLowerCase();
      const email = (item.email ?? '').toLowerCase();
      return label.includes(filter) || email.includes(filter);
    })
  });

  readonly itemIcons = computed<ReadonlyMap<string, FgaIconName>>(() =>
    new Map(
      this.filteredItems().flatMap((item) => {
        const iconName = this.resolveIconName(item);
        return iconName ? [[item.id, iconName] as const] : [];
      }),
    ),
  );

  readonly changesCount = computed<number>(() => {
    const lists = this.config().lists;

    if (this.isMembersMode()) {
      return this.getSelectionDiffCount(lists.members, this.stagedMembers());
    }

    return (
      this.getSelectionDiffCount(lists.ownerUsers, this.stagedOwnerUsers()) +
      this.getSelectionDiffCount(lists.ownerGroups, this.stagedOwnerGroups())
    );
  });

  readonly hasChanges = computed<boolean>(() => this.changesCount() > 0);

  readonly view = computed<PickerModeView>(() => PICKER_MODE_VIEWS[this.config().mode]);

  readonly isMembersMode = computed<boolean>(() => this.config().mode === MEMBERSHIP_MODE.MEMBERS);

  readonly isOwnersMode = computed<boolean>(() => this.config().mode === MEMBERSHIP_MODE.OWNERS);

  readonly typeFilterValue = computed<PickerTypeFilter>(() =>
    this.isOwnersMode() ? this.ownerFilter() : this.memberFilter(),
  );

  onOwnerFilterChange(ownerFilter: PickerOwnerFilter): void {
    this.setTypeFilter(this.ownerFilter, ownerFilter);
  }

  onMemberFilterChange(memberFilter: PickerMemberFilter): void {
    this.setTypeFilter(this.memberFilter, memberFilter);
  }

  onTypeFilterChange(value: PickerTypeFilter): void {
    this.isOwnersMode()
      ? this.onOwnerFilterChange(value as PickerOwnerFilter)
      : this.onMemberFilterChange(value as PickerMemberFilter);
  }

  onSearch(value: string): void {
    this.filterText.set(value ?? '');
  }

  onClearSearch(): void {
    this.filterText.set('');
  }

  onToggle(item: PickerItem, checked: boolean): void {
    const activeItemsSignal = this.getActiveItemsSignal(item);
    activeItemsSignal.update((items) => this.updateItemSelection(items, item.id, checked));
  }

  onSave(): void {
    this.saveChange.emit(this.buildSavePayload());
  }

  onCancel(): void {
    this.closePanel.emit();
  }

  private setTypeFilter<TFilter extends PickerTypeFilter>(filter: WritableSignal<TFilter>, value: TFilter): void {
    filter.set(value);
    this.filterText.set('');
  }

  private stageItems(
    items: PickerItem[] | undefined,
    previous?: { source: PickerItem[] | undefined; value: PickerItem[] },
  ): PickerItem[] {
    return previous && previous.source === items ? previous.value : this.cloneItems(items);
  }

  private cloneItems(items?: PickerItem[]): PickerItem[] {
    return (items ?? []).map((item) => ({ ...item }));
  }

  private resolveIconName(item: PickerItem): FgaIconName | null {
    switch (item.type) {
      case ENTITY_TYPE.GROUP:
        return isScimManagedGroupId(item.id) ? GROUP_ICON.SCIM : GROUP_ICON.LOCAL;
      case ENTITY_TYPE.M2M:
        return ENTITY_TYPE.M2M;
      case ENTITY_TYPE.USER:
      default:
        return ENTITY_TYPE.USER;
    }
  }

  private applyMemberTypeFilter(items: PickerItem[]): PickerItem[] {
    if (!this.isMembersMode()) {
      return items;
    }

    switch (this.memberFilter()) {
      case PICKER_MEMBER_FILTER.SERVICE:
        return items.filter((item) => item.type === ENTITY_TYPE.M2M);
      case PICKER_MEMBER_FILTER.USER:
        return items.filter((item) => item.type !== ENTITY_TYPE.M2M && item.type !== ENTITY_TYPE.GROUP);
      default:
        return items;
    }
  }

  private getSelectedIds(items?: PickerItem[]): Set<string> {
    return new Set((items ?? []).filter((item) => item.selected).map((item) => item.id));
  }

  private getSelectionDiffCount(original?: PickerItem[], staged?: PickerItem[]): number {
    const originalSelected = this.getSelectedIds(original);
    const stagedSelected = this.getSelectedIds(staged);
    const removed = [...originalSelected].filter((id) => !stagedSelected.has(id)).length;
    const added = [...stagedSelected].filter((id) => !originalSelected.has(id)).length;
    return removed + added;
  }

  private getActiveItemsSignal(item: PickerItem): WritableSignal<PickerItem[]> {
    if (this.isMembersMode()) {
      return this.stagedMembers;
    }

    return item.type === ENTITY_TYPE.GROUP
      ? this.stagedOwnerGroups
      : this.stagedOwnerUsers;
  }

  private updateItemSelection(items: PickerItem[], itemId: string, selected: boolean): PickerItem[] {
    return items.map((item) => item.id === itemId ? { ...item, selected } : item);
  }

  private buildSavePayload(): PickerSavePayload {
    const mode = this.config().mode;

    if (mode === MEMBERSHIP_MODE.MEMBERS) {
      return {
        lists: { members: this.cloneItems(this.stagedMembers()) },
        mode
      };
    }

    return {
      lists: {
        ownerUsers: this.cloneItems(this.stagedOwnerUsers()),
        ownerGroups: this.cloneItems(this.stagedOwnerGroups()),
      },
      mode
    };
  }
}
